import HeaderBar from './HederBar';
import { useSearchParams } from 'react-router-dom';
import { memo, useState } from 'react';
import { ReportType } from '@declarations/common/reportType';
import { Box, Button, styled } from '@mui/material';
import { useForm } from 'react-hook-form';
import { serialize } from 'object-to-formdata';
import Field from '@components/CRUDForm/Field';
import { Fields } from '@components/CRUDForm/declarations/FormField';
import { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import LegalLogRecordService from '@api/legalLogRecordService';

type Props = {
  reportType: ReportType;
  onSave?: () => void;
};

const ProgressContainer = styled('span')`
  position: absolute;
  margin-left: 8px;
  margin-top: -5px;
`;

function LegalReviewForm({ reportType, onSave }: Props) {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const id = searchParams.get('id');

  const fields: Fields<any> = [
    {
      type: 'text',
      required: false,
      name: 'note',
      label: 'Notes',
      props: {
        disabled: loading,
        multiline: true,
        minRows: 3,
        inputProps: { maxLength: 180 },
      },
    },
    {
      fullWidth: true,
      type: 'dropzone',
      name: 'attachments',
      label: 'Upload Completed Review',
      props: {
        disabled: loading,
        maxFiles: 1,
        text: 'Drag and drop a legal review here, or click to choose it, that not exceeding 50MB in size.'
      },
    },
  ];

  const form = useForm<any>({
    defaultValues: { reportId: id, reportType, subject: null },
    shouldFocusError: true,
    reValidateMode: 'onChange',
  });

  const onSubmit = form.handleSubmit(data => {
    setLoading(true);
    const formData = serialize(data, {
      dotsForObjectNotation: false,
      nullsAsUndefineds: true,
    });
    LegalLogRecordService.createRecord(formData)
      .then(() => {
        onSave && onSave();
        form.reset();
      })
      .catch(error => {
        if (error instanceof AxiosError) {
          const { response } = error;
          if (response && response.status === 400) {
            if ('message' in response.data) {
              toast.error(response.data.message);
            }
            if ('fields' in response.data) {
              response.data.fields.forEach(({ field }: { field: string; errors: string[] }) => {
                form.setError(field as any, {});
              });
            }
            return;
          }
        }
        toast.error('Unable to save entity');
      })
      .finally(() => {
        setLoading(false);
      });
  });


  const description = (
    <div>
      Download{' '}
      <a
        href={process.env.PUBLIC_URL + '/Expert Legal Review Template.docx'}
        style={{ color: '#FFF' }}
        download
      >
        Legal Review Template
      </a>{' '}
      to begin review.
    </div>
  );

  return (
    <>
      <HeaderBar title="Legal Review" description={description} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '32px', mt: '32px', maxWidth: '600px' }}>
        {fields.map(field => (
          <Field key={field.name} field={field} form={form} />
        ))}
        <Box>
          <Button disabled={loading} variant="contained" onClick={onSubmit}>
            Complete
          </Button>
        </Box>
      </Box>
    </>
  );
}

export default memo(LegalReviewForm);
